// SW-31 first-launch walkthrough (contract: hq agents/specs/SW-31-FIRST-LAUNCH-
// WALKTHROUGH-CONTRACT-2026-09-12.md). One card per field over the LIVE clock
// screen, styled like the chart tooltip (.walkthrough-* in styles.css mirrors
// .chart-tooltip). Identification only. Pure client: no network.
import { el } from "../util/dom";

/** DRAFT copy (Owner to approve); a wording swap is an edit to this array only. */
export const WALKTHROUGH_CARDS: readonly { title: string; body: string }[] = [
  { title: "RATIO", body: "The live Market Pressure Clock reading. 1.0 is balance." },
  { title: "R-HOD", body: "Ratio high of the day, with the Bitcoin price at the moment it was set." },
  { title: "R-LOD", body: "Ratio low of the day, with the Bitcoin price at the moment it was set." },
  { title: "BITCOIN", body: "Live Bitcoin price." },
  { title: "BID DEPTH · ASK DEPTH", body: "The buy side and the sell side behind the reading. Subscriber readouts." },
  { title: "24H VOL · HIGH · LOW", body: "Exchange volume over the last 24 hours, with the 24-hour price high and low." },
  { title: "SCALE", body: "RAW shows the reading as-is. NORMALIZED shows readings below 1.0 as their mirror-image negatives." },
  { title: "STATUS · MENU", body: "Connection and data-age indicator. MENU holds Account, Alerts, Feedback and this walkthrough." },
];

export const WALKTHROUGH_KEY = "bitclock-walkthrough";

/** True once SKIP/DONE was pressed with "Don't show again" checked (per browser). */
export function isWalkthroughDone(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(WALKTHROUGH_KEY) === "done";
  } catch {
    return false;
  }
}

function markDone(): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(WALKTHROUGH_KEY, "done");
  } catch {
    /* private-mode / storage-disabled: shows again next visit */
  }
}

const SWIPE_PX = 40;
const GAP_PX = 12;

/**
 * Show the walkthrough over `host` (the screen frame). `anchors[i]` lists the
 * element(s) card i outlines -- same order as WALKTHROUGH_CARDS. Always shows;
 * the first-visit gate (isWalkthroughDone) is the caller's. Returns the overlay.
 */
export function showWalkthrough(anchors: HTMLElement[][], host: HTMLElement): HTMLElement {
  const last = WALKTHROUGH_CARDS.length - 1;
  let index = 0;
  let boxTouched = false; // once the user toggles the checkbox, stop auto-setting it
  let touchStartX = 0;

  const title = el("div", { class: "walkthrough-title", id: "walkthroughTitle" });
  const body = el("div", { class: "walkthrough-body", id: "walkthroughBody" });
  const counter = el("span", { class: "walkthrough-counter" });
  const prevBtn = el("button", { class: "walkthrough-nav", type: "button", "aria-label": "Previous" }, ["‹"]);
  const nextBtn = el("button", { class: "walkthrough-nav", type: "button", "aria-label": "Next" }, ["›"]);
  const dontShow = el("input", { type: "checkbox", class: "walkthrough-checkbox" });
  const endBtn = el("button", { class: "walkthrough-end", type: "button" }, ["SKIP"]);
  const outline = el("div", { class: "walkthrough-outline" });
  const card = el(
    "div",
    {
      class: "walkthrough-card",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": "walkthroughTitle",
      "aria-describedby": "walkthroughBody",
      tabindex: "-1",
    },
    [
      el("div", { class: "walkthrough-head" }, [title, counter]),
      body,
      el("div", { class: "walkthrough-footer" }, [
        prevBtn,
        nextBtn,
        el("label", { class: "walkthrough-check" }, [dontShow, "Don't show again"]),
        endBtn,
      ]),
    ]
  );
  const root = el("div", { class: "walkthrough-scrim" }, [outline, card]);

  // ONE outline box per card over the union rect of its anchors; its huge
  // box-shadow is the scrim, so the identified field is the undimmed cutout.
  // The card sits under the field when it fits, else above. Phones (<= 520px)
  // span the width via styles.css, so only `top` is set inline there.
  function place(): void {
    const rects = anchors[index].map((a) => a.getBoundingClientRect());
    const top = Math.min(...rects.map((r) => r.top));
    const bottom = Math.max(...rects.map((r) => r.bottom));
    const left = Math.min(...rects.map((r) => r.left));
    const right = Math.max(...rects.map((r) => r.right));
    outline.style.top = `${top - 4}px`;
    outline.style.left = `${left - 4}px`;
    outline.style.width = `${right - left + 8}px`;
    outline.style.height = `${bottom - top + 8}px`;
    const fitsBelow = bottom + GAP_PX + card.offsetHeight <= window.innerHeight;
    card.style.top = `${Math.max(8, fitsBelow ? bottom + GAP_PX : top - GAP_PX - card.offsetHeight)}px`;
    card.style.left =
      window.innerWidth <= 520 ? "" : `${Math.min(Math.max(8, left), window.innerWidth - card.offsetWidth - 8)}px`;
  }

  function render(): void {
    const c = WALKTHROUGH_CARDS[index];
    title.textContent = c.title;
    body.textContent = c.body;
    counter.textContent = `${index + 1} / ${WALKTHROUGH_CARDS.length}`;
    prevBtn.disabled = index === 0;
    nextBtn.disabled = index === last;
    endBtn.textContent = index === last ? "DONE" : "SKIP";
    if (!boxTouched) dontShow.checked = index === last;
    anchors[index][0]?.scrollIntoView?.({ block: "nearest" });
    place();
    card.focus();
  }

  function go(delta: number): void {
    const next = index + delta;
    if (next < 0 || next > last) return;
    index = next;
    render();
  }

  function close(): void {
    if (dontShow.checked) markDone();
    window.removeEventListener("resize", place);
    window.removeEventListener("scroll", place);
    root.remove();
  }

  prevBtn.addEventListener("click", () => go(-1));
  nextBtn.addEventListener("click", () => go(1));
  endBtn.addEventListener("click", close);
  dontShow.addEventListener("change", () => {
    boxTouched = true;
  });
  root.addEventListener("keydown", (event) => {
    if (event.key === "ArrowRight") go(1);
    else if (event.key === "ArrowLeft") go(-1);
    else if (event.key === "Escape") close();
  });
  root.addEventListener("touchstart", (event) => {
    touchStartX = event.changedTouches[0].clientX;
  });
  root.addEventListener("touchend", (event) => {
    const dx = event.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) >= SWIPE_PX) go(dx < 0 ? 1 : -1);
  });
  // Scrim click dismisses nothing; it only returns focus so the arrow keys keep working.
  root.addEventListener("click", (event) => {
    if (event.target === root) card.focus();
  });
  window.addEventListener("resize", place);
  window.addEventListener("scroll", place);

  host.append(root);
  render();
  return root;
}
